import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import NewsCard from "@/components/feature/NewsCard";
import StatusBlock from "@/components/feature/StatusBlock";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { topics } from "@/mocks/news";
import { formatViews, getNewsByTopic, getTopicById, sortedByTime } from "@/utils/news";

type SortKey = "new" | "old";

export default function TopicPage() {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const topic = getTopicById(topicId);
  const [sort, setSort] = useState<SortKey>("new");
  const [loading, setLoading] = useState(true);

  useDocumentMeta({
    title: `${topic?.name ?? "专题"} - 爆料网专题合集`,
    description: topic
      ? `${topic.name}：${topic.subtitle}。${topic.description.slice(0, 60)}`
      : "专题不存在或已下线，返回首页浏览最新爆料。",
    keywords: topic ? `${topic.name},${topic.keywords.join(",")},爆料网专题` : "爆料网专题",
  });

  const list = useMemo(() => {
    if (!topicId) return [];
    const base = sortedByTime(getNewsByTopic(topicId));
    return sort === "new" ? base : [...base].reverse();
  }, [topicId, sort]);

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 340);
    return () => window.clearTimeout(timer);
  }, [topicId, sort]);

  if (!topic) {
    return (
      <PageLayout>
        <div className="mx-auto max-w-[900px] px-4 md:px-6 py-16">
          <StatusBlock
            icon="ri-folder-warning-line"
            title="专题不存在或已下线"
            description="该专题可能已经结束整理，回到首页可以看到正在进行中的专题。"
            actionLabel="返回首页"
            onAction={() => navigate("/")}
          />
        </div>
      </PageLayout>
    );
  }

  const totalViews = list.reduce((sum, item) => sum + item.views, 0);

  return (
    <PageLayout>
      <div className="mx-auto max-w-[1280px] px-4 md:px-6 pt-5 md:pt-7">
        <nav className="flex items-center gap-1.5 text-xs text-foreground-500 flex-wrap">
          <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          <span>专题</span>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          <span className="text-foreground-800">{topic.name}</span>
        </nav>

        <header className="mt-4 relative rounded-lg overflow-hidden bg-foreground-900 h-[300px] md:h-[360px]">
          <img
            src={topic.cover}
            alt={topic.name}
            title={`${topic.name} - 专题合集封面`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/93 via-foreground-950/60 to-foreground-950/20" />
          <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-8">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center h-6 px-2 rounded bg-accent-500 text-foreground-950 text-xs font-bold">
                专题合集
              </span>
              <span className="text-xs text-background-200/85">
                {list.length} 条爆料 · 累计 {formatViews(totalViews)} 阅读
              </span>
            </div>
            <h1 className="mt-3 text-[26px] md:text-[36px] font-black leading-tight text-background-50">
              {topic.name}
            </h1>
            <p className="mt-2 text-sm md:text-[15px] text-background-200/90">{topic.subtitle}</p>
            <p className="mt-3 hidden sm:block max-w-[720px] text-[13px] leading-7 text-background-200/80 line-clamp-2-safe">
              {topic.description}
            </p>
          </div>
        </header>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          {topic.keywords.map((word) => (
            <span
              key={word}
              className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs"
            >
              # {word}
            </span>
          ))}
          <span className="ml-auto text-xs text-foreground-400">
            按时间{sort === "new" ? "倒序" : "正序"}整理
          </span>
        </div>

        <div className="mt-7 flex flex-col lg:flex-row gap-6 lg:gap-7 items-start">
          <section className="w-full lg:flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3 pb-3 border-b border-background-200">
              <h2 className="text-xl font-black text-foreground-950">专题内爆料</h2>
              <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
                {[
                  { key: "new" as SortKey, label: "最新在前" },
                  { key: "old" as SortKey, label: "最早在前" },
                ].map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setSort(option.key)}
                    className={`px-3 py-1.5 rounded-full text-[13px] transition-colors cursor-pointer whitespace-nowrap ${
                      sort === option.key
                        ? "bg-background-50 text-primary-600 font-bold"
                        : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="mt-6 space-y-3">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="flex gap-4 p-4 rounded-lg border border-background-200/70 bg-background-50"
                  >
                    <div className="w-[224px] h-[138px] rounded-md bg-background-200 animate-pulse hidden sm:block" />
                    <div className="flex-1 space-y-3 py-1">
                      <div className="h-3 w-24 rounded bg-background-200 animate-pulse" />
                      <div className="h-4 w-3/4 rounded bg-background-200 animate-pulse" />
                      <div className="h-3 w-full rounded bg-background-200 animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            ) : list.length === 0 ? (
              <div className="mt-6">
                <StatusBlock
                  icon="ri-inbox-archive-line"
                  title="该专题暂无内容"
                  description="我们还在整理相关线索，先去看看其他专题吧。"
                  actionLabel="返回首页"
                  onAction={() => navigate("/")}
                />
              </div>
            ) : (
              <ul className="mt-4 space-y-3">
                {list.map((item) => (
                  <li key={item.id}>
                    <NewsCard item={item} />
                  </li>
                ))}
              </ul>
            )}
          </section>

          <aside className="w-full lg:w-[320px] shrink-0 space-y-5">
            <section className="rounded-lg border border-background-200/80 bg-background-50 overflow-hidden">
              <div className="px-4 h-12 flex items-center justify-between border-b border-background-200/80">
                <h2 className="text-[15px] font-bold text-foreground-950">其他专题</h2>
                <span className="font-label text-[11px] tracking-[0.18em] text-foreground-400">
                  TOPICS
                </span>
              </div>
              <div className="p-3 space-y-1">
                {topics
                  .filter((item) => item.id !== topic.id)
                  .map((item) => (
                    <Link
                      key={item.id}
                      to={`/topic/${item.id}`}
                      className="flex items-center gap-2 px-2 py-2.5 rounded-md hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <i className="ri-flashlight-line text-primary-500" />
                      <span className="text-sm text-foreground-700 truncate">{item.name}</span>
                      <i className="ri-arrow-right-s-line ml-auto text-foreground-400" />
                    </Link>
                  ))}
              </div>
            </section>

            <section className="rounded-lg border border-background-200/80 bg-background-100 p-4">
              <h2 className="text-[15px] font-bold text-foreground-950">关于专题整理</h2>
              <p className="mt-2 text-xs leading-6 text-foreground-600">
                专题由编辑根据公开信息整理，按发布时间收录同一事件的爆料。若后续出现官方通报，会补充在对应条目内。
              </p>
              <Link
                to="/about"
                className="mt-3 inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
              >
                提供补充线索
                <i className="ri-arrow-right-line" />
              </Link>
            </section>
          </aside>
        </div>
      </div>
    </PageLayout>
  );
}